import type {
  IntelligenceArtifact,
  IntelligenceReport,
  SecondOrderDomainKey,
} from '@akem/shared';
import { getApiEndpoint } from '../config/api.js';

type IntelligenceJobStatus = {
  jobId: string;
  profileId: string;
  status: 'queued' | 'running' | 'complete' | 'error';
  domain?: SecondOrderDomainKey | null;
  artifactCount?: number;
  error?: string;
  queuedAt?: string;
  startedAt?: string;
  completedAt?: string;
  lastUpdated?: string;
};

const DEFAULT_POLL_TIMEOUT_MS = 20_000;
const MIN_POLL_TIMEOUT_MS = 1_000;
const INITIAL_POLL_DELAY_MS = 400;
const MAX_POLL_DELAY_MS = 2_000;

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function abortError(): Error {
  const err = new Error('Aborted');
  // Align with Fetch abort error name for easier detection upstream
  (err as any).name = 'AbortError';
  return err;
}

function sleepWithSignal(ms: number, signal?: AbortSignal) {
  if (!signal) return sleep(ms);
  return new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(abortError());
      return;
    }
    const id = setTimeout(() => {
      cleanup();
      resolve();
    }, ms);
    const onAbort = () => {
      cleanup();
      reject(abortError());
    };
    const cleanup = () => {
      clearTimeout(id);
      signal.removeEventListener('abort', onAbort);
    };
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

function extractJobId(payload: any): string {
  if (!payload || typeof payload !== 'object') return '';
  const raw = typeof payload.jobId === 'string' ? payload.jobId.trim() : '';
  return raw.length > 0 ? raw : '';
}

function normalizeArtifacts(payload: any): IntelligenceArtifact[] {
  if (!payload || typeof payload !== 'object') return [];
  const artifacts = (payload as any).artifacts;
  return Array.isArray(artifacts) ? (artifacts as IntelligenceArtifact[]) : [];
}

export async function getIntelligenceJobStatus(
  jobId: string,
  profileId: string,
  opts?: { signal?: AbortSignal }
): Promise<IntelligenceJobStatus | null> {
  const url = new URL(`${getApiEndpoint()}/intelligence/jobs/${encodeURIComponent(jobId)}`);
  url.searchParams.set('profileId', profileId);
  const res = await fetch(url.toString(), { signal: opts?.signal });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`getIntelligenceJobStatus failed: ${res.status}`);
  const json = await res.json();
  return (json?.status as IntelligenceJobStatus) || null;
}

export async function getIntelligenceArtifacts(
  profileId: string,
  domain: SecondOrderDomainKey,
  opts?: { maxWaitMs?: number; signal?: AbortSignal }
): Promise<IntelligenceArtifact[]> {
  const url = new URL(`${getApiEndpoint()}/intelligence`);
  url.searchParams.set('profileId', profileId);
  url.searchParams.set('domain', domain);
  const res = await fetch(url.toString(), { signal: opts?.signal });
  // Fast path
  if (res.ok && res.status !== 202) {
    const json = await res.json();
    return normalizeArtifacts(json);
  }
  // Queued path: poll job status
  if (res.status === 202) {
    const json = await res.json().catch(() => ({} as any));
    const maxMs =
      typeof opts?.maxWaitMs === 'number'
        ? Math.max(MIN_POLL_TIMEOUT_MS, opts.maxWaitMs)
        : DEFAULT_POLL_TIMEOUT_MS;
    const start = Date.now();
    let response: Response | null = res;
    let payload: any = json;
    let jobId = extractJobId(payload);
    let delay = INITIAL_POLL_DELAY_MS;

    while (Date.now() - start < maxMs) {
      if (opts?.signal?.aborted) throw abortError();

      let jobCompletedThisCycle = false;
      if (jobId) {
        const status = await getIntelligenceJobStatus(jobId, profileId, {
          signal: opts?.signal,
        }).catch(() => null);
        if (status?.status === 'error') {
          throw new Error(status?.error || 'Intelligence job failed');
        }
        if (status?.status === 'complete') {
          jobId = '';
          jobCompletedThisCycle = true;
        }
      }

      if (!response) {
        response = await fetch(url.toString(), { signal: opts?.signal });
      }

      if (response.ok && response.status !== 202) {
        const readyJson = await response.json().catch(() => ({} as any));
        return normalizeArtifacts(readyJson);
      }

      if (response.status !== 202) {
        throw new Error(`getIntelligenceArtifacts failed: ${response.status}`);
      }

      payload = await response.json().catch(() => ({} as any));
      const nextJobId = extractJobId(payload);
      if (nextJobId) {
        jobId = nextJobId;
      }

      response = null;

      if (!jobCompletedThisCycle) {
        await sleepWithSignal(delay, opts?.signal);
        delay = Math.min(Math.round(delay * 1.5), MAX_POLL_DELAY_MS);
      } else {
        delay = INITIAL_POLL_DELAY_MS;
      }
    }
    throw new Error('Timed out waiting for intelligence artifacts');
  }
  throw new Error(`getIntelligenceArtifacts failed: ${res.status}`);
}

export interface IntelligenceArtifactsBatchItem {
  artifact: IntelligenceArtifact;
  sourceProfileId: string | null;
  contributingProfiles?: string[] | null;
  fallbackProfileId?: string | null;
}

export interface IntelligenceArtifactsBatchResult {
  status: number;
  items: IntelligenceArtifactsBatchItem[];
  queuedProfiles: string[];
  jobIds: string[];
  partial?: boolean;
  errors?: Array<{ profileId: string; error?: string }>;
}

export interface StorylineSummary {
  id: string;
  eventId?: string;
  instrumentId: string;
  title: string;
  domain: SecondOrderDomainKey;
  bucket?: string;
  asOf: string;
   direction?: 'positive' | 'neutral' | 'negative';
  monitorId?: string;
  sparkline?: number[];
}

export async function getIntelligenceArtifactsBatch(
  profileIds: string[],
  domain: SecondOrderDomainKey,
  opts?: { limit?: number; signal?: AbortSignal; maxWaitMs?: number }
): Promise<IntelligenceArtifactsBatchResult> {
  if (!Array.isArray(profileIds) || profileIds.length === 0) {
    throw new Error('getIntelligenceArtifactsBatch requires profileIds');
  }
  const url = new URL(`${getApiEndpoint()}/intelligence/aggregate`);
  url.searchParams.set('domain', domain);
  url.searchParams.set('profileIds', profileIds.join(','));
  if (typeof opts?.limit === 'number' && Number.isFinite(opts.limit)) {
    url.searchParams.set('limit', String(Math.max(1, Math.floor(opts.limit))));
  }

  const maxMs =
    typeof opts?.maxWaitMs === 'number'
      ? Math.max(MIN_POLL_TIMEOUT_MS, opts.maxWaitMs)
      : DEFAULT_POLL_TIMEOUT_MS;
  const start = Date.now();
  let delay = INITIAL_POLL_DELAY_MS;
  let response: Response | null = null;
  let payload: any = null;

  const parseResult = (status: number, body: any): IntelligenceArtifactsBatchResult => {
    const items = Array.isArray(body?.items)
      ? (body.items as IntelligenceArtifactsBatchItem[])
      : [];
    const queuedProfiles = Array.isArray(body?.queuedProfiles)
      ? (body.queuedProfiles as string[])
      : [];
    const jobIds = Array.isArray(body?.jobIds)
      ? (body.jobIds as string[])
      : [];
    const errors = Array.isArray(body?.errors)
      ? (body.errors as Array<{ profileId: string; error?: string }>)
      : undefined;
    const partial = Boolean(body?.partial);
    return {
      status,
      items,
      queuedProfiles,
      jobIds,
      partial,
      errors,
    };
  };

  while (Date.now() - start < maxMs) {
    if (opts?.signal?.aborted) throw abortError();
    if (!response) {
      response = await fetch(url.toString(), { signal: opts?.signal });
    }

    if (response.status === 404) {
      const err = new Error('Batch intelligence endpoint unavailable');
      (err as any).code = 'BATCH_UNAVAILABLE';
      throw err;
    }

    if (!response.ok && response.status !== 202) {
      throw new Error(`getIntelligenceArtifactsBatch failed: ${response.status}`);
    }

    payload = await response.json().catch(() => ({}));
    if (response.status !== 202) {
      return parseResult(response.status, payload);
    }

    response = null;
    await sleepWithSignal(delay, opts?.signal);
    delay = Math.min(Math.round(delay * 1.5), MAX_POLL_DELAY_MS);
  }

  throw new Error('Timed out waiting for intelligence artifacts batch');
}

export async function getProfileStorylines(
  profileId: string,
  domain: SecondOrderDomainKey,
  opts?: { limit?: number; signal?: AbortSignal }
): Promise<StorylineSummary[]> {
  const url = new URL(`${getApiEndpoint()}/intelligence/storylines/profile`);
  url.searchParams.set('profileId', profileId);
  url.searchParams.set('domain', domain);
  if (typeof opts?.limit === 'number' && Number.isFinite(opts.limit)) {
    url.searchParams.set('limit', String(Math.max(1, Math.floor(opts.limit))));
  }
  const res = await fetch(url.toString(), { signal: opts?.signal });
  if (!res.ok) {
    throw new Error(`getProfileStorylines failed: ${res.status}`);
  }
  const json = await res.json().catch(() => ({} as any));
  const items = Array.isArray(json?.items) ? (json.items as StorylineSummary[]) : [];
  return items;
}

export async function getIntelligenceReport(
  artifactId: string,
  profileId: string,
  opts?: { signal?: AbortSignal }
): Promise<{ artifact: IntelligenceArtifact; report: IntelligenceReport } | null> {
  const url = new URL(`${getApiEndpoint()}/intelligence/artifacts/${encodeURIComponent(artifactId)}`);
  url.searchParams.set('profileId', profileId);
  const res = await fetch(url.toString(), { signal: opts?.signal });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`getIntelligenceReport failed: ${res.status}`);
  const json = await res.json();
  return json as { artifact: IntelligenceArtifact; report: IntelligenceReport };
}

export async function refreshIntelligence(profileId: string, domains?: SecondOrderDomainKey[]): Promise<{ ack: boolean; queued?: boolean; local?: boolean }>{
  const res = await fetch(`${getApiEndpoint()}/intelligence/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ profileId, domains })
  });
  if (!res.ok) throw new Error(`refreshIntelligence failed: ${res.status}`);
  const json = await res.json();
  return { ack: Boolean(json?.ack), queued: json?.queued, local: json?.local };
}

export interface IntelligenceArtifactsListResponse {
  items: IntelligenceArtifact[];
  nextCursor: string | null;
}

export async function listIntelligenceArtifacts(
  profileId: string,
  domain: SecondOrderDomainKey,
  opts?: { limit?: number; cursor?: string | null; signal?: AbortSignal }
): Promise<IntelligenceArtifactsListResponse> {
  const url = new URL(`${getApiEndpoint()}/intelligence/list`);
  url.searchParams.set('profileId', profileId);
  url.searchParams.set('domain', domain);
  if (typeof opts?.limit === 'number' && Number.isFinite(opts.limit)) {
    url.searchParams.set('limit', String(Math.max(1, Math.floor(opts.limit))));
  }
  if (opts?.cursor) {
    url.searchParams.set('cursor', opts.cursor);
  }

  const res = await fetch(url.toString(), { signal: opts?.signal });
  if (!res.ok) {
    throw new Error(`listIntelligenceArtifacts failed: ${res.status}`);
  }

  const json = await res.json().catch(() => ({}));
  const items = Array.isArray(json?.items)
    ? (json.items as IntelligenceArtifact[])
    : [];
  const nextCursor = typeof json?.nextCursor === 'string' && json.nextCursor
    ? (json.nextCursor as string)
    : null;

  return { items, nextCursor };
}
